/**
 * Guest face session state — in-memory only (shared kiosk / WebView safe).
 */

import {
  clearSalonGuestPii,
  getSalonGuestPiiField,
  purgeLegacySalonPiiSessionKeys,
  setSalonGuestPii,
} from './salonGuestPiiStore';

export const SALON_ACTIVE_CHECKIN_ID_KEY = 'enterprise_salon_active_checkin_id';
export const SALON_ACTIVE_CHECKIN_CODE_KEY = 'enterprise_salon_active_checkin_code';
export const SALON_ACTIVE_CHECKIN_STATUS_KEY = 'enterprise_salon_active_checkin_status';
export const SALON_CHECKIN_UPDATED_EVENT = 'salon-active-checkin-updated';
export const SALON_VISIT_HANDOFF_KEY = 'enterprise_salon_visit_handoff';

export const SALON_FACE_SESSION_KEYS = [
  'enterprise_salon_face_checked_in',
  'enterprise_salon_face_user_id',
  'enterprise_salon_face_org',
  'enterprise_salon_compare_previous',
  'enterprise_salon_visit_reference',
  'enterprise_salon_entry_source',
  SALON_ACTIVE_CHECKIN_ID_KEY,
  SALON_ACTIVE_CHECKIN_CODE_KEY,
  SALON_ACTIVE_CHECKIN_STATUS_KEY,
  SALON_VISIT_HANDOFF_KEY,
  'enterprise_salon_selected_services',
  'enterprise_salon_selected_service_names',
  'enterprise_salon_pending_checkin_id',
  'enterprise_salon_pending_checkin_code',
];

const mem = {
  faceCheckedIn: false,
  faceUserId: '',
  faceOrg: '',
  comparePrevious: '',
  visitReference: '',
  entrySource: '',
  activeCheckinId: '',
  activeCheckinCode: '',
  activeCheckinStatus: '',
  activeCheckinFaceUserId: '',
  visitHandoff: null,
  selectedServices: null,
  selectedServiceNames: null,
  pendingCheckinId: '',
  pendingCheckinCode: '',
};

function emitCheckinUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(SALON_CHECKIN_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

export function purgeLegacySalonFaceSessionStorage() {
  try {
    SALON_FACE_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

function purgeLegacyFaceSessionStorage() {
  purgeLegacySalonFaceSessionStorage();
}

export function isSalonFaceCheckedIn() {
  return Boolean(mem.faceCheckedIn);
}

export function getSalonFaceUserId() {
  return mem.faceUserId || '';
}

export function getSalonFaceOrg() {
  return mem.faceOrg || '';
}

export function getSalonComparePreviousFlag() {
  return mem.comparePrevious === '1';
}

export function getSalonVisitReference() {
  return mem.visitReference || '';
}

export function getSalonEntrySource() {
  return mem.entrySource || '';
}

export function setSalonFaceUserId(value) {
  mem.faceUserId = String(value || '').trim();
}

export function setSalonComparePrevious(isPrevious) {
  mem.comparePrevious = isPrevious ? '1' : '0';
}

export function setSalonFaceOrg(value) {
  mem.faceOrg = String(value || '').trim();
}

export function setSalonVisitReference(value) {
  mem.visitReference = String(value || '').trim();
}

export function setSalonEntrySource(value) {
  mem.entrySource = String(value || '').trim();
}

export function setSalonPendingCheckin({ id, code } = {}) {
  if (id != null && String(id).trim() !== '') {
    mem.pendingCheckinId = String(id);
  }
  if (code != null && String(code).trim() !== '') {
    mem.pendingCheckinCode = String(code).trim();
  }
}

export function getSalonPendingCheckinId() {
  return mem.pendingCheckinId || '';
}

export function clearSalonPendingCheckin() {
  mem.pendingCheckinId = '';
  mem.pendingCheckinCode = '';
}

export function setActiveCheckinSession({ id, code, status, faceUserId } = {}) {
  if (id != null && String(id).trim() !== '') {
    mem.activeCheckinId = String(id);
  }
  if (code != null && String(code).trim() !== '') {
    mem.activeCheckinCode = String(code).trim();
  }
  if (status != null && String(status).trim() !== '') {
    mem.activeCheckinStatus = String(status).trim().toLowerCase();
  }
  if (faceUserId != null && String(faceUserId).trim() !== '') {
    mem.activeCheckinFaceUserId = String(faceUserId).trim();
  }
  emitCheckinUpdated();
}

export function getActiveCheckinSession() {
  if (!mem.activeCheckinId) return null;
  return {
    id: mem.activeCheckinId,
    code: mem.activeCheckinCode || '',
    status: (mem.activeCheckinStatus || 'in_service').toLowerCase(),
    faceUserId: mem.activeCheckinFaceUserId || '',
  };
}

export function clearActiveCheckinSession() {
  mem.activeCheckinId = '';
  mem.activeCheckinCode = '';
  mem.activeCheckinStatus = '';
  mem.activeCheckinFaceUserId = '';
  emitCheckinUpdated();
}

export function clearVisitLocalState() {
  clearActiveCheckinSession();
  clearVisitHandoff();
  mem.selectedServices = null;
  mem.selectedServiceNames = null;
}

/** Hard reset after a finished service visit so the next stylist starts clean. */
export function clearDeviceAfterVisitFinish() {
  clearVisitLocalState();
  mem.faceCheckedIn = false;
  mem.faceUserId = '';
  mem.comparePrevious = '';
  mem.visitReference = '';
  mem.pendingCheckinId = '';
  mem.pendingCheckinCode = '';
  purgeLegacyFaceSessionStorage();
  purgeLegacySalonPiiSessionKeys();
  clearSalonGuestPii();
  emitCheckinUpdated();
}

export function readVisitHandoff() {
  return mem.visitHandoff ? { ...mem.visitHandoff } : null;
}

export function writeVisitHandoff(payload = {}) {
  const prev = mem.visitHandoff || {};
  mem.visitHandoff = {
    ...prev,
    ...payload,
    updatedAt: new Date().toISOString(),
  };
  return { ...mem.visitHandoff };
}

export function clearVisitHandoff() {
  mem.visitHandoff = null;
}

export function persistSelectedServicesForRecommendations(services) {
  if (!Array.isArray(services) || !services.length) return;
  const names = services
    .map((s) => (typeof s === 'string' ? s : s?.name || s?.label || s?.service_name || ''))
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  if (!names.length) return;
  mem.selectedServices = services;
  mem.selectedServiceNames = names;
}

export function readSessionSelectedServices() {
  if (Array.isArray(mem.selectedServices) && mem.selectedServices.length) {
    return mem.selectedServices.map((s) => (typeof s === 'object' && s ? { ...s } : s));
  }
  const handoff = readVisitHandoff();
  const fromHandoff = handoff?.selectedServices;
  return Array.isArray(fromHandoff) ? fromHandoff.map((s) => (typeof s === 'object' && s ? { ...s } : s)) : [];
}

export function readSelectedServiceNames() {
  if (Array.isArray(mem.selectedServiceNames) && mem.selectedServiceNames.length) {
    return mem.selectedServiceNames.map((n) => String(n || '').trim().toLowerCase()).filter(Boolean);
  }
  const handoff = readVisitHandoff();
  const services = handoff?.selectedServices || [];
  return services
    .map((s) => (typeof s === 'string' ? s : s?.name || s?.label || ''))
    .map((n) => String(n || '').trim().toLowerCase())
    .filter(Boolean);
}

export function clearSalonFaceSession() {
  mem.faceCheckedIn = false;
  mem.faceUserId = '';
  mem.faceOrg = '';
  mem.comparePrevious = '';
  mem.visitReference = '';
  mem.entrySource = '';
  mem.activeCheckinId = '';
  mem.activeCheckinCode = '';
  mem.activeCheckinStatus = '';
  mem.activeCheckinFaceUserId = '';
  mem.visitHandoff = null;
  mem.selectedServices = null;
  mem.selectedServiceNames = null;
  mem.pendingCheckinId = '';
  mem.pendingCheckinCode = '';
  purgeLegacyFaceSessionStorage();
  purgeLegacySalonPiiSessionKeys();
  clearSalonGuestPii();
  emitCheckinUpdated();
}

export function buildSalonVisitReference(faceUserId, publicToken) {
  const uid = String(faceUserId || 'guest').replace(/\W/g, '').slice(0, 16) || 'guest';
  const tok = String(publicToken || '').replace(/\W/g, '').slice(0, 10) || Date.now().toString(36);
  return `SV-${uid}-${tok}-${Date.now().toString(36)}`;
}

export function markSalonFaceCheckedIn(fields = {}) {
  mem.faceCheckedIn = true;
  if (fields.faceUserId) mem.faceUserId = String(fields.faceUserId);
  if (fields.comparePrevious != null) {
    mem.comparePrevious = fields.comparePrevious ? '1' : '0';
  }
  setSalonGuestPii({
    mobile: fields.mobile,
    name: fields.name,
    email: fields.email,
    gender: fields.gender,
    dob: fields.dob,
    age: fields.age,
  });
  purgeLegacySalonPiiSessionKeys();
  if (fields.organizationId) mem.faceOrg = String(fields.organizationId);
  if (fields.visitReference) mem.visitReference = String(fields.visitReference);
  if (fields.entrySource) mem.entrySource = String(fields.entrySource);
  if (fields.checkinId != null) {
    setActiveCheckinSession({
      id: fields.checkinId,
      code: fields.checkinCode,
      status: fields.checkinStatus || 'in_service',
      faceUserId: fields.faceUserId,
    });
  }
}

/** Gender from in-memory guest PII (replaces legacy sessionStorage key). */
export function getSalonFaceGender() {
  return getSalonGuestPiiField('gender') || '';
}
